import { z } from 'zod';
import { CurrencyCodeSchema, IdSchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
import { ProjectStatusSchema } from './projects.js';
export const WeekStartSchema = z.enum(['monday', 'sunday', 'saturday']);
export const DefaultDateRangeSchema = z.enum([
    'today',
    'this_week',
    'this_month',
    'this_quarter',
    'this_year',
    'last_30_days',
]);
let supportedTimezones;
let supportedCurrencies;
/** IANA zone names known to the runtime. `UTC` is always accepted. */
export function isSupportedTimezone(value) {
    supportedTimezones ??= new Set(Intl.supportedValuesOf('timeZone'));
    return value === 'UTC' || supportedTimezones.has(value);
}
/** ISO 4217 codes known to the runtime. */
export function isSupportedCurrency(value) {
    supportedCurrencies ??= new Set(Intl.supportedValuesOf('currency'));
    return supportedCurrencies.has(value);
}
/** Canonical BCP 47 tag for a locale, or null when the value is not a language tag. */
export function canonicalLocale(value) {
    try {
        return Intl.getCanonicalLocales(value)[0] ?? null;
    }
    catch {
        return null;
    }
}
export const LocaleSchema = z
    .string()
    .trim()
    .min(2)
    .max(35)
    .transform((value, context) => {
    const canonical = canonicalLocale(value);
    if (!canonical) {
        context.addIssue({
            code: 'custom',
            message: 'Locale must be a BCP 47 language tag such as en-US',
        });
        return z.NEVER;
    }
    return canonical;
});
export const TimezoneSchema = z.string().trim().min(1).max(64).refine(isSupportedTimezone, {
    message: 'Timezone must be an IANA zone name such as America/New_York',
});
export const SettingsCurrencySchema = CurrencyCodeSchema.refine(isSupportedCurrency, {
    message: 'Currency must be an ISO 4217 code such as USD',
});
/**
 * Optional capabilities a tenant can switch on or off (specification 2.2). Restoration
 * tenants start with all of them; a roofing tenant might keep only equipment.
 */
export const CapabilityKeySchema = z.enum([
    'loss_events',
    'insurance_claims',
    'restoration_presets',
    'equipment',
]);
export const DEFAULT_ENABLED_CAPABILITIES = CapabilityKeySchema.options;
export const OrganizationSettingsSchema = z.strictObject({
    organizationId: IdSchema,
    locale: z.string(),
    timezone: z.string(),
    currency: CurrencyCodeSchema,
    weekStart: WeekStartSchema,
    defaultDateRange: DefaultDateRangeSchema,
    enabledCapabilities: z.array(CapabilityKeySchema),
    /** 0 means the organization still uses defaults and no row has been written. */
    version: VersionSchema,
    createdAt: TimestampSchema.nullable(),
    updatedAt: TimestampSchema.nullable(),
});
export const UpdateOrganizationSettingsRequestSchema = z.strictObject({
    locale: LocaleSchema.optional(),
    timezone: TimezoneSchema.optional(),
    currency: SettingsCurrencySchema.optional(),
    weekStart: WeekStartSchema.optional(),
    defaultDateRange: DefaultDateRangeSchema.optional(),
    enabledCapabilities: z
        .array(CapabilityKeySchema)
        .max(CapabilityKeySchema.options.length)
        .optional(),
    version: VersionSchema,
});
/** Organization-owned reference catalogs (specification 8.2). */
export const ReferenceDefinitionKeySchema = z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/, 'Use lowercase letters, digits, and single underscores')
    .max(80);
export const ColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a #RRGGBB color');
export const ReferenceDefinitionSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    key: z.string(),
    label: z.string(),
    description: z.string().nullable(),
    sortOrder: z.number().int(),
    active: z.boolean(),
    color: z.string().nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const ProjectStatusDefinitionSchema = ReferenceDefinitionSchema.extend({
    category: ProjectStatusSchema,
});
export const CreateReferenceDefinitionRequestSchema = z.strictObject({
    key: ReferenceDefinitionKeySchema,
    label: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().default(null),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    color: ColorSchema.nullable().default(null),
});
export const UpdateReferenceDefinitionRequestSchema = z.strictObject({
    label: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    active: z.boolean().optional(),
    color: ColorSchema.nullable().optional(),
    version: VersionSchema,
});
export const CreateProjectStatusDefinitionRequestSchema = CreateReferenceDefinitionRequestSchema.extend({ category: ProjectStatusSchema });
export const UpdateProjectStatusDefinitionRequestSchema = UpdateReferenceDefinitionRequestSchema.extend({ category: ProjectStatusSchema.optional() });
/** Full ordering of the catalog; every active and inactive definition must be listed once. */
export const ReorderReferenceDefinitionsRequestSchema = z.strictObject({
    orderedIds: z.array(IdSchema).min(1).max(500),
});
export const ReferenceDefinitionListQuerySchema = z.strictObject({
    cursor: IdSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(100),
    includeInactive: z.stringbool().default(false),
});
export const ReferenceDefinitionListSchema = z.strictObject({
    items: z.array(ReferenceDefinitionSchema),
    page: PageInfoSchema,
});
export const ProjectStatusDefinitionListSchema = z.strictObject({
    items: z.array(ProjectStatusDefinitionSchema),
    page: PageInfoSchema,
});
